import React, { createContext, useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import JsSIP from 'jssip';
import { useApi } from 'app/hooks';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import ApiResolver from 'http/apis/ApiResolver';
import {
    TRANSFER_POLL_INTERVAL_MS,
    SUPERVISE_FEATURE_CODES,
    SUPERVISE_ACL_KEY
} from 'app/main/pages/PBXCalls.js/pbxConstants';
import { logWsCloseReason } from './sipWsKeepAlive';

export const PBXCallState = createContext(null);

const PBX_ICE_SERVERS = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'turn:uatwebrtc.its.com.pk:3478', username: 'itsvoice', credential: 'ItsVoice@123' },
    { urls: 'turn:uatwebrtc.its.com.pk:3478?transport=tcp', username: 'itsvoice', credential: 'ItsVoice@123' },
    { urls: 'turns:uatwebrtc.its.com.pk:5349', username: 'itsvoice', credential: 'ItsVoice@123' }
];

// SIPml withheld the SDP offer/answer until ICE gathering reached 'complete',
// so an unreachable TURN server could stall a call past the point where
// Asterisk gave up, and the app had to monkey-patch the library at runtime to
// force the SDP out (patchIceGatheringTimeout, now deleted). SIP.js takes the
// same deadline as a first-class option and resolves gathering with whatever
// candidates it has by then.
const ICE_GATHERING_TIMEOUT_MS = 4000;

// Media constraints for every session — this softphone is audio only.
const PBX_MEDIA_CONSTRAINTS = { audio: true, video: false };

/**
 * Inbound caller IDs arrive with a dialing prefix in front of the real number — "00"
 * from the trunk (00923040909099) or "920" once it has been through the customer
 * lookup's 0 -> 92 rewrite (920923040909099). Drop either one so both the name and the
 * number show the same digits.
 *
 * Anything else is left untouched — extensions, foreign numbers and SIP display names.
 */
const stripTrunkPrefix = (raw) => {
    if (!raw) return raw;

    const value = String(raw);
    if (/^920\d+$/.test(value)) return value.slice(3);
    if (/^00\d+$/.test(value)) return value.slice(2);
    return value;
};

/** Normalize ws_server from the API — avoid `/ws/ws` if it already ends with /ws. */
const buildWebsocketProxyUrl = (wsServer) => {
    const base = String(wsServer || '').replace(/\/+$/, '');
    if (!base) return '';
    if (base.endsWith('/ws')) return base;
    return `${base}/ws`;
};

function PBXCallContext({ children }) {
    const [softPhoneOpen, setSoftPhoneOpen] = useState(false);
    const [registered, setRegistered] = useState(false);
    const [callState, setCallState] = useState('idle');
    // True from the moment Accept is clicked until the session actually
    // reaches 'active' (or fails). answer() doesn't resolve until SDP
    // negotiation — ICE gathering in particular — finishes, so without this
    // the incoming-call screen sits on "Incoming call..." with no feedback
    // for that whole stretch and looks like the click did nothing.
    const [isAnswering, setIsAnswering] = useState(false);
    const answeringRef = useRef(false);
    // Which supervisor picker (monitor/coaching/conference) is currently
    // waiting on an agent selection — null when none is open. Only ever set
    // from idle; see openSuperviseAgentPicker.
    const [superviseMode, setSuperviseMode] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [remoteNumber, setRemoteNumber] = useState('');
    const [callDuration, setCallDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [enablePBXCallRingtone, setEnablePBXCallRingtone] = useState(() => {
        const stored = localStorage.getItem('EnablePBXCallTone');
        return stored !== null ? JSON.parse(stored) : true;
    });
    const [isRingtonePlaying, setIsRingtonePlaying] = useState(false);

    const [sipConfig, setSipConfig] = useState(null);
    const [accountInfo, setAccountInfo] = useState(null);
    const [customerInfo, setCustomerInfo] = useState(null);

    const [sipStatus, setSipStatus] = useState('disconnected');

    const [autoAnswer, setAutoAnswer] = useState(() => {
        const stored = localStorage.getItem('PBXAutoAnswer');
        return stored !== null ? JSON.parse(stored) : false;
    });

    const [keypadSound, setKeypadSound] = useState(() => {
        const stored = localStorage.getItem('PBXKeypadSound');
        return stored !== null ? JSON.parse(stored) : true;
    });

    const [isRecording, setIsRecording] = useState(false);
    const [callDirection, setCallDirection] = useState(null);

    const [agentStatus, setAgentStatus] = useState(() => {
        const stored = localStorage.getItem('PBXAgentStatus');
        return stored || 'available';
    });
    // SIP delegate/session handlers are closures captured at UserAgent-creation
    // time — they'd otherwise see a stale agentStatus if it changes later
    // without the user agent being torn down. Auto-reconnect needs the real value.
    const agentStatusRef = useRef(agentStatus);
    // Incremented on every user-initiated status change. A backend status
    // request started earlier must not overwrite a newer Available/Offline
    // selection when its response arrives later.
    const agentStatusVersionRef = useRef(0);
    useEffect(() => {
        agentStatusRef.current = agentStatus;
    }, [agentStatus]);

    // Same reason as agentStatusRef: the session/delegate callbacks below are
    // closures that outlive the render they were created in. Anything they read
    // that can change during a call has to come from a ref.
    const isMutedRef = useRef(isMuted);
    useEffect(() => {
        isMutedRef.current = isMuted;
    }, [isMuted]);

    const sipConfigRef = useRef(null);
    useEffect(() => {
        sipConfigRef.current = sipConfig;
    }, [sipConfig]);

    let agentPermissions = JSON.parse(localStorage.getItem('user_acl'));

    // Gates the Monitor/Coaching/Conference dial-pad feature: the logged-in
    // portal user must be a portal admin (max_token_count -1, same test
    // AgentTable uses to label a row "Admin"), the PBX identity shown top-left
    // of the dialer must itself be an admin extension, and the ACL key must
    // be granted — created under Settings -> System Permissions and assigned
    // to the role, same as every other FRONT: permission.
    let portalUserData = null;
    try {
        portalUserData = JSON.parse(localStorage.getItem('user_data'));
    } catch (err) {
        portalUserData = null;
    }
    const isPortalAdminUser = portalUserData?.max_token_count === -1;
    const isPbxAdminAccount = /admin/i.test(accountInfo?.displayName || '');
    const hasSuperviseAcl = agentPermissions?.[SUPERVISE_ACL_KEY] === 1;
    const canUseSupervise = isPortalAdminUser && isPbxAdminAccount && hasSuperviseAcl;

    const { request } = useApi();
    const requestRef = useRef(request);
    useEffect(() => {
        requestRef.current = request;
    }, [request]);

    const callTimerRef = useRef(null);
    const audioRemoteRef = useRef(null);
    const userAgentRef = useRef(null);
    const sessionRef = useRef(null);
    const registererRef = useRef(null);
    // sipConnect() is async (UserAgent.start() and Registerer.register() both
    // return promises) and is called from several places — an effect, the agent
    // status switch, the end of a call, the reconnect timer. Without this latch
    // two overlapping calls could each get past the `userAgentRef.current` guard
    // before either had finished assigning it, leaving two user agents and two
    // registrations fighting over the same AOR.
    const sipConnectingRef = useRef(false);
    // A connect asked for while one was already in flight, replayed once that
    // attempt settles. See sipConnect().
    const sipConnectRequestedRef = useRef(false);
    // Backend "connected" must follow RegistererState.Registered, not the Available click.
    const sipRegisteredRef = useRef(false);
    // Set while we are deliberately tearing the user agent down. UserAgent.stop()
    // and Registerer.unregister() both surface through the same disconnect /
    // Unregistered paths an unexpected failure does, and without this flag the
    // teardown would immediately schedule a reconnect against itself.
    const intentionalStopRef = useRef(false);
    // A user agent that had to be abandoned mid-call (see
    // handleUnexpectedDisconnect) and is stopped once the call is over.
    const orphanedUserAgentRef = useRef(null);
    const ringtoneRef = useRef(null);
    const localStreamRef = useRef(null);
    const keypadAudioRef = useRef(null);

    // =================== Recording Refs ===================
    const recorderRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const recordingMetaRef = useRef({ mimeType: '', startedAt: null, callId: null, sipCallId: null });
    const recordingStartedRef = useRef(false);
    const pbxCallIdRef = useRef(null);
    // Backend callId keyed by sipCallId. Event responses can land after the call
    // is torn down, so the id has to stay tied to the call it belongs to instead
    // of to whatever call happens to be active when the response arrives.
    const callIdBySipRef = useRef({});
    const micStreamRef = useRef(null);

    // Set while a blind transfer's REFER is in flight, cleared once the PBX
    // reports the outcome in a NOTIFY (or the safety timeout fires).
    const pendingTransferRef = useRef(null);
    const transferTimeoutRef = useRef(null);
    const [isTransferring, setIsTransferring] = useState(false);

    const callDataRef = useRef({
        sipCallId: null,
        direction: null,
        fromNumber: null,
        toNumber: null,
        fromExtension: null,
        toExtension: null,
        fromTag: null,
        toTag: null,
        startTime: null,
        answerTime: null,
        endTime: null,
        status: null,
        channelId: null,
        xChannelId: null,

    });

    // ============================================
    // PBX config loader
    // ============================================
    const loadPbxConfig = () => {
        let userAcl = null;
        try {
            userAcl = JSON.parse(localStorage.getItem('user_acl'));
        } catch (err) {
            return;
        }

        if (!userAcl) {
            return;
        }

        if (userAcl['FRONT:/apps/callcenter/dialer'] !== 1) {
            return;
        }

        if (sipConfig) {
            return;
        }

        requestRef.current({
            module: 'pbx',
            method: 'getPbxAccountConfig',
            payload: {},
            onSuccess: (response) => {
                const account = response?.data?.data?.account;
                if (!account) {
                    console.error('PBX config: no account data in response');
                    return;
                }

                if (!account.is_active) {
                    return;
                }

                const sipPassword = account.sip_password || account.sipPassword;
                if (!sipPassword) {
                    console.error(
                        'PBX config: sip_password missing from account — SIP registration will fail'
                    );
                }

                const config = {
                    realm: account.sip_realm,
                    impi: account.sip_username,
                    impu: `sip:${account.sip_username}@${account.sip_realm}`,
                    password: sipPassword || account.sip_username,
                    websocket_proxy_url: buildWebsocketProxyUrl(account.ws_server),
                    display_name: account.display_name,
                };

                setSipConfig(config);
                setAccountInfo({
                    accountId: account.id,
                    clientId: account.client_id,
                    userId: account.user_id,
                    extension: account.extension,
                    displayName: account.display_name,
                });
            },
            onError: (message) => {
                console.error('Failed to load PBX config:', message);
            }
        });
    };

    useEffect(() => {
        loadPbxConfig();
    }, []);

    // ============================================
    // Agent state <-> backend state mapping
    // ============================================
    const uiToBackendState = (uiStatus) => {
        if (uiStatus === 'available') return 'connected';
        if (uiStatus === 'break') return 'break';
        if (uiStatus === 'offline') return 'disconnected';
        return 'disconnected';
    };

    const backendToUiState = (backendState) => {
        if (backendState === 'connected') return 'available';
        if (backendState === 'break') return 'break';
        if (backendState === 'disconnected') return 'offline';
        return 'available';
    };

    // ============================================
    // Send agent state change to backend
    // ============================================
    const sendAgentStateChange = (uiStatus, reason = null, metadata = null) => {
        const state = uiToBackendState(uiStatus);
        const payload = { state };

        if (reason) payload.reason = reason;
        if (metadata) payload.metadata = metadata;

        requestRef.current({
            module: 'pbx',
            method: 'pbxAgentStateChange',
            payload,
            onSuccess: (response) => {
            },
            onError: (message) => {
                console.error('Failed to update agent state:', message);
            }
        });
    };

    // ============================================
    // Fetch saved agent state from backend
    // ============================================
    const fetchAgentState = () => {
        const requestedAtVersion = agentStatusVersionRef.current;

        requestRef.current({
            module: 'pbx',
            method: 'pbxAgentStateGet',
            payload: {},
            onSuccess: (response) => {
                if (requestedAtVersion !== agentStatusVersionRef.current) {
                    return;
                }

                const savedState = response?.data?.data?.state;
                if (!savedState) {
                    return;
                }

                const uiStatus = backendToUiState(savedState);

                localStorage.setItem('PBXAgentStatus', uiStatus);
                agentStatusRef.current = uiStatus;
                setAgentStatus(uiStatus);

                if (uiStatus === 'available') {
                    // sipConnect will be called once sipConfig loads
                } else if (uiStatus === 'break') {
                    setTimeout(() => {
                        if (
                            requestedAtVersion === agentStatusVersionRef.current &&
                            agentStatusRef.current === 'break'
                        ) {
                            sipBreak();
                        }
                    }, 100);
                } else if (uiStatus === 'offline') {
                    setTimeout(() => {
                        if (
                            requestedAtVersion === agentStatusVersionRef.current &&
                            agentStatusRef.current === 'offline'
                        ) {
                            sipDisconnect();
                        }
                    }, 100);
                }
            },
            onError: (message) => {
                console.error('Failed to fetch agent state:', message);
            }
        });
    };

    // ============================================
    // Live agent presence (for call transfer target list)
    // ============================================
    const [presenceAgents, setPresenceAgents] = useState([]);
    const [presenceLoading, setPresenceLoading] = useState(false);

    const fetchAgentPresence = () => {
        setPresenceLoading(true);
        requestRef.current({
            module: 'pbx',
            method: 'pbxAgentPresenceList',
            payload: {},
            onSuccess: (response) => {
                const agents = response?.data?.data?.agents || [];
                setPresenceAgents(agents);
                setPresenceLoading(false);
            },
            onError: (message) => {
                console.error('Failed to fetch agent presence:', message);
                setPresenceLoading(false);
            }
        });
    };

    // Poll while a call is active — that's the only time the transfer picker
    // is visible — or while the supervisor agent picker is open, so there's
    // no reason to keep hitting this endpoint at rest.
    useEffect(() => {
        if (callState !== 'active' && !superviseMode) return;

        fetchAgentPresence();
        const interval = setInterval(fetchAgentPresence, TRANSFER_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [callState, superviseMode]);

    // Any transition away from idle (an incoming call arriving, say) closes a
    // still-open supervisor picker rather than leaving it to reappear stale
    // once the call that interrupted it ends.
    useEffect(() => {
        if (callState !== 'idle' && superviseMode) {
            setSuperviseMode(null);
        }
    }, [callState]);

    // ============================================
    // Disconnect on browser tab close / refresh
    // ============================================
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (sipStatus !== 'connected' && sipStatus !== 'break') return;

            // Best-effort SIP teardown on the way out. Neither of these can be
            // awaited — the page is already going away — but both get their
            // request onto the still-open WebSocket before the browser tears it
            // down, which is enough for the PBX to release the extension.
            // UserAgent.stop() also BYEs any session it still holds, so the
            // explicit terminate above is only there to cover a session the
            // user agent has already forgotten about.
            intentionalStopRef.current = true;
            if (sessionRef.current) {
                try { terminateSession(sessionRef.current); } catch (_) { }
                sessionRef.current = null;
            }
            if (userAgentRef.current) {
                try { userAgentRef.current.stop(); } catch (_) { }
                userAgentRef.current = null;
            }
            registererRef.current = null;

            // Build the same request CoreHttpHandler would make
            const resolvedApi = ApiResolver.resolve('pbx', 'pbxAgentStateChange');
            const apiUrl = `${process.env.REACT_APP_API_ENDPOINT}${resolvedApi.path}`;

            const headers = {
                'Content-Type': 'application/json',
                ...resolvedApi.headers,
            };

            // Fill in tokens from localStorage (same as CoreHttpHandler)
            if (headers['xt-client-token'] === null) {
                headers['xt-client-token'] = localStorage.getItem('client_token');
            }
            if (headers['xt-user-token'] === null) {
                headers['xt-user-token'] = localStorage.getItem('user_token');
            }

            fetch(apiUrl, {
                method: resolvedApi.method?.toUpperCase() || 'POST',
                headers,
                body: JSON.stringify({ state: 'disconnected' }),
                keepalive: true,
            }).catch(() => { });
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [sipStatus]);

    useEffect(() => {
        if (!sipConfig) return;
        fetchAgentState();
    }, [sipConfig]);

    // ============================================
    // Check WhatsApp onboard status
    // ============================================
    const whatsappOnboardedRef = useRef(null);

    const checkWhatsAppOnboarded = () => {
        requestRef.current({
            module: 'channels',
            method: 'whatsappOnboardCheck',
            payload: {},
            onSuccess: (response) => {
                const isOnboarded = response?.data?.data?.whatsapp?.isOnboarded === true;
                whatsappOnboardedRef.current = isOnboarded;
            },
            onError: (message) => {
                console.error('Failed to check WhatsApp onboard:', message);
                whatsappOnboardedRef.current = false;
            }
        });
    };

    useEffect(() => {
        // checkWhatsAppOnboarded();
    }, []);

    // ============================================
    // Disconnect check for logout
    // ============================================
    const disconnectForLogout = ({ skipConfirm = false } = {}) => {
        return new Promise((resolve) => {
            // Check if user has PBX permission and is connected
            let userAcl = null;
            try {
                userAcl = JSON.parse(localStorage.getItem('user_acl'));
            } catch (err) {
                resolve(true); // Can't parse ACL, allow logout
                return;
            }

            const hasPbxAccess = userAcl?.['FRONT:/apps/callcenter/dialer'] === 1;

            if (!hasPbxAccess || sipStatus === 'disconnected' || sipStatus === 'offline') {
                resolve(true); // No PBX or already disconnected, allow logout
                return;
            }

            const disconnectAndResolve = () => {
                // Disconnect SIP
                sipDisconnect();

                // Update agent state to disconnected on backend
                sendAgentStateChange('offline');

                // Update local state
                localStorage.setItem('PBXAgentStatus', 'offline');
                setAgentStatus('offline');

                // Small delay to let the API call fire before page redirects
                setTimeout(() => resolve(true), 500);
            };

            // The idle-timeout "Still there?" dialog's Logout button auto-disconnects
            // without stacking a second confirmation on top of its own modal.
            if (skipConfirm) {
                disconnectAndResolve();
                return;
            }

            // User is connected or on break — ask to disconnect first
            Swal.fire({
                title: 'Disconnect from Call Center?',
                text: sipStatus === 'break'
                    ? 'You are currently on break. Disconnect and logout?'
                    : 'You are currently connected to the call center. Disconnect and logout?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ff3b30',
                cancelButtonColor: '#8e8e93',
                confirmButtonText: 'Disconnect & Logout',
                cancelButtonText: 'Cancel',
                backdrop: 'rgba(0,0,0,0.4)',
                heightAuto: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    disconnectAndResolve();
                } else {
                    resolve(false); // User cancelled, don't logout
                }
            });
        });
    };
    // ============================================
    // Search for customer by phone number (PBX)
    // ============================================
    const searchCustomerByNumber = (phoneNumber) => {
        if (!phoneNumber) {
            return;
        }

        let searchNumber = phoneNumber;
        if (searchNumber.startsWith('0')) {
            searchNumber = '92' + searchNumber.slice(1);
        }

        requestRef.current({
            module: 'pbx',
            method: 'pbxCustomerGet',
            payload: {
                channel_type: 'pbx',
                search: searchNumber,
                page: 0,
                limit: 1
            },
            onSuccess: (response) => {
                const list = response?.data?.data?.list?.data || [];

                if (list.length > 0) {
                    const customer = list[0];
                    const info = {
                        customerId: customer.id,
                        // Auto-created records are named after the prefixed caller ID.
                        name: stripTrunkPrefix(customer.name || ''),
                        phoneNumber: customer.phone_number,
                        channelType: customer.channel_type,
                        attributes: customer.attributes || []
                    };
                    setCustomerInfo(info);
                } else {
                    setCustomerInfo(null);
                }
            },
            onError: (message) => {
                console.error('Failed PBX customer search:', message);
                setCustomerInfo(null);
            }
        });
    };

    // ============================================
    // Update PBX customer info
    // ============================================
    const updateCustomerInfo = (customerId, name, attributes, onSuccess, onError) => {
        if (!customerId) {
            onError?.('No customer ID');
            return;
        }

        const payload = {
            id: Number.parseInt(customerId),
            name: name?.trim() || undefined,
            attributes: attributes || []
        };

        requestRef.current({
            module: 'pbx',
            method: 'pbxCustomerUpdate',
            payload,
            onSuccess: (response) => {
                const updated = response?.data?.data?.customer;
                if (updated) {
                    const info = {
                        customerId: updated.id,
                        name: updated.name || '',
                        phoneNumber: updated.phone_number,
                        channelType: updated.channel_type,
                        attributes: updated.attributes || []
                    };
                    setCustomerInfo(info);
                    onSuccess?.(info);
                }
            },
            onError: (message) => {
                console.error('Failed to update customer:', message);
                onError?.(message);
            }
        });
    };

    // ============================================
    // Send call event API
    // ============================================
    const sendCallEvent = (eventType, extra = null) => {
        const data = callDataRef.current;

        if (!data.sipCallId) {
            return;
        }

        // Pin the call this event belongs to so a late response is applied to
        // that call and not to the next one.
        const eventSipCallId = data.sipCallId;

        const payload = {
            sipCallId: data.sipCallId,
            eventType,
            direction: data.direction,
            fromNumber: data.fromNumber,
            toNumber: data.toNumber,
            fromExtension: data.fromExtension,
            toExtension: data.toExtension,
            fromTag: data.fromTag,
            toTag: data.toTag,
            occurredAt: new Date().toISOString(),
            clientId: accountInfo?.clientId,
            userId: accountInfo?.userId,
            payload: {
                userAgent: navigator.userAgent,
                codec: 'opus'
            }
        };

        // e.g. { transferTo, transferToName } for a 'transferred' event.
        if (extra) {
            Object.assign(payload, extra);
        }

        // Include channelId only for inbound calls
        if (data.direction === 'inbound' && data.xChannelId) {
            payload.xChannelId = data.xChannelId;
        }

        requestRef.current({
            module: 'pbx',
            method: 'pbxCallEvents',
            payload,
            onSuccess: (response) => {
                // Capture backend callId for recording attachment
                const returnedId = response?.data?.data?.callId;
                if (!returnedId) {
                    return;
                }

                callIdBySipRef.current[eventSipCallId] = returnedId;

                // The 'ended' response arrives after handleCallEnd already reset
                // pbxCallIdRef. Without this check it would revive the finished
                // call's id and every later call would attach its recording to
                // the previous call.
                if (callDataRef.current.sipCallId === eventSipCallId) {
                    pbxCallIdRef.current = returnedId;
                }

                const trackedSipIds = Object.keys(callIdBySipRef.current);
                if (trackedSipIds.length > 20) {
                    delete callIdBySipRef.current[trackedSipIds[0]];
                }
            },
            onError: (message) => {
                console.error(`Failed to log ${eventType}:`, message);
            }
        });
    };

    /**
     * Records the SIP dialog identifiers the CDR events are keyed on. SIPml had
     * to be reached into for these (e.o_event.o_message.o_hdr_Call_ID etc.);
     * SIP.js exposes them as plain properties on the session's initial request
     * and, once the call is up, on the confirmed dialog.
     *
     * Safe to call repeatedly — every field is written at most once with a real
     * value, so a later call cannot blank out what an earlier one captured.
     */
    const captureDialogIdentity = (session) => {
        if (!session) return;

        const data = callDataRef.current;
        // Invitation -> IncomingRequestMessage, Inviter -> OutgoingRequestMessage.
        // Both carry callId / fromTag / toTag with From and To in wire order, so
        // this reads the same way for either direction.
        const request = session.request || session['_request'];

        if (request) {
            const callId = request.call_id || request.callId;
            const fromTag = request.from_tag || request.fromTag || session['_from_tag'];
            const toTag = request.to_tag || request.toTag || session['_to_tag'];
            if (callId && !data.sipCallId) {
                data.sipCallId = callId;
            }
            if (fromTag && !data.fromTag) {
                data.fromTag = fromTag;
            }
            if (toTag && !data.toTag) {
                data.toTag = toTag;
            }
        }

        // The To tag does not exist until the far end answers — on an outbound
        // call it only turns up on the 200 OK, long after the INVITE read above.
        // The confirmed dialog has both halves, in local/remote terms rather
        // than From/To, so map them back by direction.
        const dialog = session['_dialog'] || session.dialog;
        if (!dialog) return;

        const dialogId = dialog.id || dialog;
        if (!data.sipCallId) {
            data.sipCallId = dialogId.call_id || dialogId.callId;
        }
        if (data.direction === 'outbound') {
            if (!data.fromTag) data.fromTag = dialogId.local_tag || dialogId.localTag;
            if (dialogId.remote_tag || dialogId.remoteTag) {
                data.toTag = dialogId.remote_tag || dialogId.remoteTag;
            }
        } else {
            if (!data.fromTag) data.fromTag = dialogId.remote_tag || dialogId.remoteTag;
            if (dialogId.local_tag || dialogId.localTag) {
                data.toTag = dialogId.local_tag || dialogId.localTag;
            }
        }
    };

    // ============================================
    // Ringtone setup
    // ============================================
    useEffect(() => {
        const audio = new Audio('/assets/sounds/ring-notification.mp3');
        audio.loop = true;
        audio.muted = true;
        ringtoneRef.current = audio;

        const unlockAudio = () => {
            audio.muted = false;
            document.removeEventListener('click', unlockAudio);
            document.removeEventListener('keydown', unlockAudio);
        };

        document.addEventListener('click', unlockAudio);
        document.addEventListener('keydown', unlockAudio);

        return () => {
            document.removeEventListener('click', unlockAudio);
            document.removeEventListener('keydown', unlockAudio);
        };
    }, []);

    // ============================================
    // Keypad tap sound setup
    // ============================================
    // useEffect(() => {
    //     const audio = new Audio('/assets/sounds/keypad-sound.mp3');
    //     audio.volume = 1;
    //     audio.preload = 'auto';

    //     keypadAudioRef.current = audio;

    //     const unlockKeypadAudio = () => {
    //         audio.play().then(() => {
    //             audio.pause();
    //             audio.currentTime = 0;
    //         }).catch(() => { });
    //         document.removeEventListener('click', unlockKeypadAudio);
    //         document.removeEventListener('keydown', unlockKeypadAudio);
    //     };

    //     document.addEventListener('click', unlockKeypadAudio);
    //     document.addEventListener('keydown', unlockKeypadAudio);

    //     return () => {
    //         document.removeEventListener('click', unlockKeypadAudio);
    //         document.removeEventListener('keydown', unlockKeypadAudio);
    //     };
    // }, []);

    useEffect(() => {
        const audio = new Audio('/assets/sounds/keypad-sound.mp3');
        audio.volume = 1;
        audio.preload = 'auto';

        keypadAudioRef.current = audio;
    }, []);
    
    useEffect(() => {
        const ringtone = ringtoneRef.current;
        if (!ringtone) return;

        const shouldPlayRingtone = enablePBXCallRingtone && callState === 'incoming';

        if (shouldPlayRingtone) {
            ringtone.loop = true;
            ringtone.play().catch(() => { });
            setIsRingtonePlaying(true);
        } else {
            if (!ringtone.paused) {
                stopRingtone();
            }
        }
    }, [callState, enablePBXCallRingtone]);

    const stopRingtone = () => {
        if (ringtoneRef.current) {
            ringtoneRef.current.pause();
            ringtoneRef.current.currentTime = 0;
        }
        setIsRingtonePlaying(false);
    };

    const handlePBXCallRingtone = () => {
        localStorage.setItem('EnablePBXCallTone', !enablePBXCallRingtone);
        setEnablePBXCallRingtone(!enablePBXCallRingtone);
    };

    // ============================================
    // Auto-reconnect on unexpected disconnect
    // ============================================
    // SIP.js ships its own reconnect (UserAgentOptions.reconnectionAttempts) but
    // it is marked deprecated, defaults to 0 attempts, and only fires for a
    // transport-level drop — it would not cover a registration that dies while
    // the socket stays up. Keep the app's own backoff, which does: retries are
    // capped, and only run while the agent still intends to be available.
    const RECONNECT_BASE_DELAY_MS = 3000;
    const RECONNECT_MAX_DELAY_MS = 30000;
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimeoutRef = useRef(null);

    const handleUnexpectedDisconnect = (reason) => {
        // The transport's onDisconnect and the registerer dropping out of the
        // Registered state can both fire for the same underlying failure —
        // without this guard that schedules two competing reconnect attempts,
        // which race each other's REGISTER and can look like an auth failure
        // server-side even though the credentials are fine.
        if (reconnectTimeoutRef.current) {
            return;
        }

        // A deliberate teardown (sipDisconnect / sipBreak / logout / unload)
        // travels the exact same paths as a failure. Nothing to recover from.
        if (intentionalStopRef.current) {
            return;
        }

        setRegistered(false);
        sipRegisteredRef.current = false;
        clearReRegister();

        const hasActiveCall = !!sessionRef.current;

        // The transport is dead either way, so always clear its bookkeeping
        // here — this used to happen only below the active-call check,
        // which left userAgentRef.current pointing at the dead stack for the
        // rest of the call. handleCallEnd()'s `!userAgentRef.current` check
        // then believed a stack still existed and silently skipped
        // reconnecting, leaving the agent unregistered for every call after
        // the one that hit the drop, not just that one.
        //
        // But do NOT call .stop() on it while a call is active. This was true
        // of SIPml.Stack.stop() and is equally true of UserAgent.stop(): it
        // walks every session the user agent still holds and terminates it —
        // BYE on the wire, SessionDescriptionHandler.close() on the
        // PeerConnection — so calling it here would hang up on the customer,
        // which is exactly the bug that was fixed once already. The transport
        // is dead at this point regardless (that is why we are in this
        // handler), so there is nothing for .stop() to usefully do to it —
        // drop the reference and let it get GC'd; a fresh UserAgent is built
        // from scratch on reconnect anyway.
        const deadUserAgent = userAgentRef.current;
        // Detach first, then stop. Every delegate callback checks that the user
        // agent it fires for is still the current one, so clearing the ref up
        // front makes stop()'s own asynchronous disconnect callback a no-op
        // instead of a second trip through this handler.
        userAgentRef.current = null;
        registererRef.current = null;

        if (deadUserAgent) {
            if (hasActiveCall) {
                // Can't stop it yet without killing the call, but it must not
                // simply be dropped either: unlike a transport that has already
                // died, a user agent that got here because its REGISTRATION
                // failed still has a live socket, and building a second one
                // alongside it would put two registrations on the same AOR.
                // handleCallEnd() stops it once the call is really over.
                orphanedUserAgentRef.current = deadUserAgent;
            } else {
                // stop() is async; a sync catch would not contain its rejection.
                Promise.resolve().then(() => deadUserAgent.stop()).catch(() => { });
            }
        }

        // Don't touch the live call session itself, and don't attempt to
        // reconnect yet — that risks colliding with the still-active call.
        // handleCallEnd() calls sipConnect() once the call actually ends,
        // and now correctly sees userAgentRef.current as null when it does.
        if (hasActiveCall) {
            console.warn(`SIP disconnected unexpectedly (${reason}) during an active call; will reconnect once it ends.`);
            return;
        }

        if (agentStatusRef.current !== 'available') {
            return;
        }

        const attempt = reconnectAttemptsRef.current;
        const delay = Math.min(RECONNECT_BASE_DELAY_MS * (2 ** attempt), RECONNECT_MAX_DELAY_MS);
        reconnectAttemptsRef.current = attempt + 1;

        console.warn(`SIP disconnected unexpectedly (${reason}); reconnecting in ${delay / 1000}s (attempt ${attempt + 1})`);

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            if (agentStatusRef.current === 'available' && !userAgentRef.current) {
                sipConnect();
            }
        }, delay);
    };

    // ============================================
    // Re-registration
    // ============================================
    // Losing the registration binding is a much smaller failure than losing the
    // transport, and it gets a much smaller response: send another REGISTER on
    // the socket we already have. Only when the socket itself is gone, or the
    // registerer has been disposed, do we fall back to rebuilding everything.
    const REREGISTER_BASE_DELAY_MS = 2000;
    const REREGISTER_MAX_DELAY_MS = 30000;
    const REREGISTER_MAX_ATTEMPTS = 10;
    const reregisterAttemptsRef = useRef(0);
    const reregisterTimeoutRef = useRef(null);

    const scheduleReRegister = (userAgent) => {
        if (intentionalStopRef.current) return;
        if (reregisterTimeoutRef.current) return;
        if (agentStatusRef.current !== 'available') return;
        if (registererRef.current !== userAgent) return;

        // No socket to register over — that is the transport's problem, and
        // its own disconnect handling owns the recovery.
        if (!userAgent.isConnected()) {
            handleUnexpectedDisconnect('registration lost while transport down');
            return;
        }

        const attempt = reregisterAttemptsRef.current;
        if (attempt >= REREGISTER_MAX_ATTEMPTS) {
            console.error(
                `SIP re-registration failed ${attempt} times; rebuilding the connection.`
            );
            reregisterAttemptsRef.current = 0;
            handleUnexpectedDisconnect('re-registration exhausted');
            return;
        }

        const delay = Math.min(REREGISTER_BASE_DELAY_MS * (2 ** attempt), REREGISTER_MAX_DELAY_MS);
        reregisterAttemptsRef.current = attempt + 1;
        console.warn(`SIP registration lost; re-registering in ${delay / 1000}s (attempt ${attempt + 1})`);

        reregisterTimeoutRef.current = setTimeout(() => {
            reregisterTimeoutRef.current = null;

            if (intentionalStopRef.current) return;
            if (registererRef.current !== userAgent) return;
            if (agentStatusRef.current !== 'available') return;

            try {
                userAgent.register();
            } catch (err) {
                console.error('Re-registration failed:', err?.message || err);
            }
        }, delay);
    };

    const clearReRegister = () => {
        reregisterAttemptsRef.current = 0;
        if (reregisterTimeoutRef.current) {
            clearTimeout(reregisterTimeoutRef.current);
            reregisterTimeoutRef.current = null;
        }
    };

    // ============================================
    // SIP Connect / Disconnect / Break
    // ============================================
    const sipConnect = async () => {
        if (!sipConfig) return;
        if (userAgentRef.current) return;

        if (sipConnectingRef.current) {
            // An attempt is already in flight. Record that another connect was
            // asked for rather than dropping it: if the in-flight one bails out
            // — which is exactly what happens when the agent flips to Offline
            // and back while the socket is still opening — that request would
            // otherwise be lost and the agent would sit disconnected while the
            // UI insists they are available.
            sipConnectRequestedRef.current = true;
            return;
        }

        sipConnectingRef.current = true;
        sipConnectRequestedRef.current = false;

        try {
            await sipConnectAttempt();
        } finally {
            sipConnectingRef.current = false;
        }

        if (sipConnectRequestedRef.current && !userAgentRef.current) {
            sipConnectRequestedRef.current = false;
            sipConnect();
        }
    };

    const sipConnectAttempt = async () => {
        // Any teardown still settling belongs to the user agent we just replaced,
        // and that one is already detached and identity-checked out of every
        // callback. Clear the latch so a failure on the NEW connection is seen.
        intentionalStopRef.current = false;

        // A retry is about to be superseded by this attempt. Leaving it armed
        // would let it fire against the user agent created here and, worse, make
        // handleUnexpectedDisconnect's double-schedule guard swallow a genuine
        // failure of that user agent — parking a dead one in the ref for good.
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        clearReRegister();

        let socket;
        let userAgent;
        try {
            socket = new JsSIP.WebSocketInterface(sipConfig.websocket_proxy_url);
            userAgent = new JsSIP.UA({
                sockets: [socket],
                uri: sipConfig.impu,
                password: sipConfig.password,
                display_name: sipConfig.impi,
                register: true,
                session_timers: false,
                user_agent: 'ITS-VoicePortal/1.0',
                contact_uri: `sip:${sipConfig.impi}@${sipConfig.realm};transport=wss`
            });
        } catch (err) {
            console.error('Failed to create SIP user agent:', err);
            return;
        }

        JsSIP.debug.disable('JsSIP:*');
        userAgentRef.current = userAgent;
        registererRef.current = userAgent;

        userAgent.on('connected', () => {
            if (userAgentRef.current !== userAgent) return;
            logWsCloseReason(socket);
        });

        userAgent.on('registered', () => {
            if (userAgentRef.current !== userAgent) return;
            setRegistered(true);
            reconnectAttemptsRef.current = 0;
            reregisterAttemptsRef.current = 0;
            if (!sipRegisteredRef.current && agentStatusRef.current === 'available') {
                sendAgentStateChange('available');
            }
            sipRegisteredRef.current = true;
        });

        userAgent.on('unregistered', () => {
            if (userAgentRef.current !== userAgent) return;
            setRegistered(false);
            sipRegisteredRef.current = false;
            scheduleReRegister(userAgent);
        });

        userAgent.on('registrationFailed', (event) => {
            if (userAgentRef.current !== userAgent) return;
            console.error('SIP registration failed:', event?.cause || event);
            setRegistered(false);
            sipRegisteredRef.current = false;
            scheduleReRegister(userAgent);
        });

        userAgent.on('disconnected', (event) => {
            if (userAgentRef.current !== userAgent) return;
            handleUnexpectedDisconnect(
                `transport disconnected: ${event?.error?.message || event?.cause || 'unknown reason'}`
            );
        });

        userAgent.on('newRTCSession', (event) => {
            if (userAgentRef.current !== userAgent) {
                try { event.session.terminate(); } catch (_) { }
                return;
            }
            if (event.originator !== 'remote') return;
            console.info('[PBX] SIP INVITE received on WebSocket');
            handleIncomingInvite(event.session);
        });

        try {
            userAgent.start();
        } catch (err) {
            console.error('Failed to connect SIP transport:', err);
            if (userAgentRef.current === userAgent) {
                userAgentRef.current = null;
                registererRef.current = null;
                handleUnexpectedDisconnect('transport failed to start');
            }
        }
    };

    const sipDisconnect = async () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        clearReRegister();

        // Everything below is deliberate, and every one of these teardown steps
        // surfaces through the same callbacks a failure would. Latch first so
        // handleUnexpectedDisconnect doesn't fight the disconnect it is watching.
        intentionalStopRef.current = true;

        const userAgent = userAgentRef.current;
        const session = sessionRef.current;
        const orphaned = orphanedUserAgentRef.current;

        userAgentRef.current = null;
        registererRef.current = null;
        sessionRef.current = null;
        orphanedUserAgentRef.current = null;
        setRegistered(false);
        sipRegisteredRef.current = false;

        if (session) {
            try { terminateSession(session); } catch (err) { }
            // The session's stateChange listener is identity-guarded on
            // sessionRef, which was just cleared, so its Terminated event will
            // be ignored — close the call out here instead. Without this the
            // 'ended' CDR event is never sent, the duration timer keeps
            // ticking and the recording is never stopped or uploaded.
            // Deliberately inside the intentionalStopRef window: handleCallEnd
            // would otherwise reconnect the user agent we are disconnecting.
            handleCallEnd();
        }

        if (userAgent) {
            try {
                userAgent.stop();
            } catch (_) {
                // References and UI state were already cleared above.
            }
        }

        if (orphaned) {
            try {
                await orphaned.stop();
            } catch (_) {
                // The orphan may already have a closed transport.
            }
        }

        // Do not clear a newer connection's intentional-stop latch if the user
        // selected Available again while this asynchronous teardown was running.
        if (!userAgentRef.current) {
            intentionalStopRef.current = false;
        }
    };

    const sipBreak = () => {
        sipDisconnect();
    };

    // ============================================
    // SIP initialization (waits for config)
    // ============================================
    // SIPml was a global installed by a <script> tag, so this used to poll for
    // window.SIPml before it could do anything. SIP.js is a module import — it
    // is there the moment this file is evaluated — so the only thing left to
    // wait for is the account config coming back from the API.
    // agentStatus is a dependency, not just a read: it starts from localStorage
    // and is then corrected by fetchAgentState from the backend. Keyed on
    // [sipConfig] alone, an agent stored locally as 'break' but reported
    // 'available' by the backend would never register — the effect had already
    // run and closed over the stale value. sipConnect() is idempotent, so the
    // extra runs this causes are free.
    useEffect(() => {
        if (!sipConfig) return;
        if (agentStatus !== 'available') return;

        sipConnect();
    }, [sipConfig, agentStatus]);

    useEffect(() => {
        if (agentStatus === 'break') {
            setSipStatus('break');
            return;
        }
        setSipStatus(registered ? 'connected' : 'disconnected');
    }, [registered, agentStatus]);

    // ============================================
    // Call Recording (local + remote audio)
    // ============================================
    const startCallRecording = async (session, attempt = 0) => {
        if (recordingStartedRef.current) return;
        // The call this was started for is over — either it ended while the
        // retry ladder below was still waiting for a remote track, or a new
        // call has already taken its place. Without this the recorder could
        // start after the fact: a microphone left open with nothing to stop it,
        // and its audio ultimately attached to the wrong call.
        if (sessionRef.current !== session) return;

        // Capture local audio via getUserMedia
        let localStream = micStreamRef.current;
        if (!localStream) {
            try {
                localStream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: false,
                });
            } catch (err) {
                console.error('getUserMedia failed for recording:', err);
                return;
            }

            // Awaiting the permission prompt gives the call time to end. Hand
            // the microphone straight back rather than parking it in the ref,
            // where stopCallRecording has already been and gone.
            if (sessionRef.current !== session) {
                try { localStream.getTracks().forEach((t) => t.stop()); } catch (_) { }
                return;
            }

            micStreamRef.current = localStream;
        }

        // JsSIP exposes the PeerConnection directly on RTCSession.connection.
        const hasAudio = (stream) => !!stream && stream.getAudioTracks().length > 0;
        const sessionRemoteStream = session?.pbxRemoteStream;
        const elementStream = audioRemoteRef.current?.srcObject;

        let remoteStream = null;
        if (hasAudio(sessionRemoteStream)) {
            remoteStream = sessionRemoteStream;
        } else if (hasAudio(elementStream)) {
            remoteStream = elementStream;
        }

        if (!remoteStream) {
            if (attempt < 10) {
                setTimeout(() => startCallRecording(session, attempt + 1), 300);
                return;
            }
            return;
        }

        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const destination = audioContext.createMediaStreamDestination();

            try {
                audioContext.createMediaStreamSource(localStream).connect(destination);
            } catch (err) {
            }
            try {
                audioContext.createMediaStreamSource(remoteStream).connect(destination);
            } catch (err) {
            }

            const candidates = [
                'audio/ogg;codecs=opus',
                'audio/ogg',
            ];
            let mimeType = '';
            for (const c of candidates) {
                if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) {
                    mimeType = c;
                    break;
                }
            }
            const options = mimeType ? { mimeType } : {};
            const mediaRecorder = new MediaRecorder(destination.stream, options);

            recordedChunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
            };

            mediaRecorder.onstop = () => {
                const chunks = recordedChunksRef.current;
                // Snapshot the meta: the upload outlives this call, and a new
                // call would overwrite the ref while it is still in flight.
                const meta = { ...recordingMetaRef.current };
                const type = meta.mimeType || 'audio/ogg';
                if (chunks && chunks.length > 0) {
                    const blob = new Blob(chunks, { type });
                    uploadAndAttachRecording(blob, meta);
                }
                recordedChunksRef.current = [];
                try { audioContext.close(); } catch (_) { }
            };

            mediaRecorder.onerror = (ev) => console.error('MediaRecorder error:', ev);

            mediaRecorder.start(1000);

            recorderRef.current = { mediaRecorder, audioContext };
            recordingMetaRef.current = {
                mimeType: mimeType || 'audio/ogg',
                startedAt: Date.now(),
                callId: null,
                sipCallId: callDataRef.current.sipCallId,
            };
            recordingStartedRef.current = true;
        } catch (err) {
            console.error('Failed to start call recording:', err);
        }
    };

    const stopCallRecording = () => {
        const rec = recorderRef.current;
        if (rec) {
            // handleCallEnd clears callDataRef and pbxCallIdRef right after this,
            // while onstop and the upload run later — so stamp the identity now.
            recordingMetaRef.current.callId = pbxCallIdRef.current;
            recordingMetaRef.current.sipCallId =
                callDataRef.current.sipCallId || recordingMetaRef.current.sipCallId;
            try {
                if (rec.mediaRecorder.state !== 'inactive') rec.mediaRecorder.stop();
            } catch (err) {
            }
        }
        recorderRef.current = null;
        recordingStartedRef.current = false;

        if (micStreamRef.current) {
            try {
                micStreamRef.current.getTracks().forEach((t) => t.stop());
            } catch (_) { }
            micStreamRef.current = null;
        }
    };

    const uploadAndAttachRecording = (blob, meta = {}) => {
        if (!blob || blob.size === 0) return;

        const sipCallId = meta.sipCallId || 'unknown';
        const sourceType = blob.type || meta.mimeType || 'audio/ogg';
        const startedAt = meta.startedAt || Date.now();
        const durationSec = Math.max(1, Math.floor((Date.now() - startedAt) / 1000));

        const filename = `pbx_call_${sipCallId}_${new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/:/g, '-')}.ogg`;

        const audioFile = new File([blob], filename, { type: sourceType });
        const formData = new FormData();
        formData.append('file', audioFile);
        formData.append('template_upload', 'false');

        CoreHttpHandler.request(
            'content',
            'upload',
            { params: formData },
            (response) => {
                const uploadedUrl = response?.data?.data?.link;
                if (!uploadedUrl) {
                    console.error('Upload succeeded but no link returned');
                    return;
                }

                // Resolve as late as possible: on a short call the event response
                // can land after the recorder stopped. Both sources are keyed to
                // this recording's own call, never to the active one.
                const callId = meta.callId || callIdBySipRef.current[sipCallId];
                if (!callId) {
                    return;
                }

                const payload = {
                    callId,
                    recordingUrl: uploadedUrl,
                    durationSec,
                    mime: sourceType,
                    sizeBytes: blob.size,
                };

                requestRef.current({
                    module: 'pbx',
                    method: 'pbxCallRecording',
                    payload,
                    onSuccess: (res) => {
                        delete callIdBySipRef.current[sipCallId];
                    },
                    onError: (message) => {
                        console.error('pbxCallRecording failed:', message);
                    },
                });
            },
            (error) => {
                console.error('Failed to upload PBX recording:', error);
            }
        );
    };

    // ============================================
    // SIP session wiring
    // ============================================

    /** Ends a JsSIP RTCSession using the correct CANCEL, rejection, or BYE. */
    const terminateSession = (session) => {
        if (!session) return Promise.resolve();
        try {
            if (typeof session.isEnded === 'function' && session.isEnded()) {
                return Promise.resolve();
            }
            const options =
                session.direction === 'incoming' &&
                !(typeof session.isEstablished === 'function' && session.isEstablished())
                    ? { status_code: 486, reason_phrase: 'Busy Here' }
                    : undefined;
            session.terminate(options);
        } catch (err) {
            console.warn('Failed to terminate SIP session cleanly:', err?.message || err);
        }
        return Promise.resolve();
    };

    /** Points the shared audio element at the JsSIP session's remote tracks. */
    const attachRemoteAudio = (session, peerConnection = null) => {
        const el = audioRemoteRef.current;
        const pc = peerConnection || session?.connection;
        if (!el || !pc) return;

        let remoteStream = session.pbxRemoteStream;
        if (!remoteStream) {
            remoteStream = new MediaStream();
            session.pbxRemoteStream = remoteStream;
        }
        pc.getReceivers?.().forEach((receiver) => {
            if (
                receiver.track &&
                !remoteStream.getTracks().some((track) => track.id === receiver.track.id)
            ) {
                remoteStream.addTrack(receiver.track);
            }
        });

        const play = () => {
            const playback = el.play();
            if (playback && typeof playback.catch === 'function') {
                // Autoplay is only refused when the page has never been
                // interacted with, which the ringtone unlock handler covers.
                playback.catch(() => { });
            }
        };

        if (el.srcObject !== remoteStream) {
            el.autoplay = true;
            el.srcObject = remoteStream;
        }

        play();
    };

    /**
     * Applies a mute state to a session's outgoing audio.
     *
     * Toggling the sender track keeps this a
     * purely local operation — no re-INVITE, so the call is not put on hold and
     * the remote end simply receives silence.
     */
    const applyMuteToSession = (session, muted) => {
        const pc = session?.connection;
        if (!pc || typeof pc.getSenders !== 'function') return false;

        let applied = false;
        pc.getSenders().forEach((sender) => {
            if (sender.track && sender.track.kind === 'audio') {
                sender.track.enabled = !muted;
                applied = true;
            }
        });
        return applied;
    };

    /**
     * Logs a warning if the media path dies while the call is still up.
     *
     * Worth having specifically because of `earlyMedia: true` on outbound calls
     * (see dialOutbound): SIP.js applies the answer from the first provisional
     * response and then ACKs the 200 OK *without* applying its SDP. That is the
     * same thing SIPml did, and it is what makes ringback audible — but if this
     * PBX ever answers from a different media endpoint than it played ringback
     * from, the connection stays pointed at the dead early-media address and
     * the call is silent while the UI shows it as active and timing.
     *
     * Deliberately only reports: 'disconnected' frequently recovers on its own,
     * and dropping a live call on a transient ICE blip would be worse than the
     * problem. This turns "silent and invisible" into "silent and diagnosable".
     */
    const watchIceConnection = (session) => {
        const pc = session?.connection;
        if (!pc || pc.pbxIceWatched) return;
        pc.pbxIceWatched = true;
        pc.addEventListener('iceconnectionstatechange', () => {
            if (pc.iceConnectionState === 'failed') {
                console.warn('SIP call ICE connection failed — the call is up but no audio is flowing.');
            }
        });
    };

    /** Subscribes to a JsSIP RTCSession lifecycle. */
    const attachSessionHandlers = (session) => {
        if (!session || session.pbxHandlersAttached) return;
        session.pbxHandlersAttached = true;

        session.on('peerconnection', (event) => {
            const pc = event.peerconnection || session.connection;
            if (!pc) return;
            watchIceConnection(session);
            pc.addEventListener('track', (trackEvent) => {
                let remoteStream = session.pbxRemoteStream;
                if (!remoteStream) {
                    remoteStream = new MediaStream();
                    session.pbxRemoteStream = remoteStream;
                }
                trackEvent.streams?.forEach((stream) => {
                    stream.getTracks().forEach((track) => {
                        if (!remoteStream.getTracks().some((item) => item.id === track.id)) {
                            remoteStream.addTrack(track);
                        }
                    });
                });
                if (
                    trackEvent.track &&
                    !remoteStream.getTracks().some((track) => track.id === trackEvent.track.id)
                ) {
                    remoteStream.addTrack(trackEvent.track);
                }
                attachRemoteAudio(session, pc);
            });
            attachRemoteAudio(session, pc);
        });

        // JsSIP waits for ICE gathering to complete before sending SDP. TURN
        // failures must not hold an INVITE or answer indefinitely.
        session.on('icecandidate', (event) => {
            if (session.pbxIceReadyTimer) return;
            session.pbxIceReadyTimer = setTimeout(() => {
                session.pbxIceReadyTimer = null;
                try { event.ready(); } catch (_) { }
            }, ICE_GATHERING_TIMEOUT_MS);
        });

        session.on('progress', () => {
            if (sessionRef.current !== session) return;
            captureDialogIdentity(session);
            attachRemoteAudio(session);
            if (isPreAnswerStatus()) {
                callDataRef.current.status = 'progress';
                sendCallEvent('progress');
            }
        });

        const onEstablished = () => {
            if (sessionRef.current !== session || session.pbxEstablishedHandled) return;
            session.pbxEstablishedHandled = true;
            captureDialogIdentity(session);
            attachRemoteAudio(session);
            watchIceConnection(session);

            if (isPreAnswerStatus()) {
                callDataRef.current.status = 'progress';
                sendCallEvent('progress');
            }
            applyMuteToSession(session, isMutedRef.current);
            setCallState('active');
            answeringRef.current = false;
            setIsAnswering(false);
            stopRingtone();
            startTimer();
            callDataRef.current.answerTime = new Date().toISOString();
            callDataRef.current.status = 'answered';
            sendCallEvent('answered');

            const localStream = new MediaStream();
            session.connection?.getSenders?.().forEach((sender) => {
                if (sender.track) localStream.addTrack(sender.track);
            });
            if (localStream.getTracks().length) localStreamRef.current = localStream;
            startCallRecording(session);
        };

        session.on('accepted', onEstablished);
        session.on('confirmed', onEstablished);

        const onEnded = () => {
            if (sessionRef.current !== session || session.pbxEndedHandled) return;
            session.pbxEndedHandled = true;
            if (session.pbxIceReadyTimer) {
                clearTimeout(session.pbxIceReadyTimer);
                session.pbxIceReadyTimer = null;
            }
            if (pendingTransferRef.current) {
                const pending = pendingTransferRef.current;
                clearTransferPending();
                callDataRef.current.status = 'transferred';
                callDataRef.current.transferTo = pending.extension;
                callDataRef.current.transferToName = pending.displayName;
            }
            handleCallEnd();
        };

        session.on('ended', onEnded);
        session.on('failed', onEnded);
    };

    // ============================================
    // Incoming call
    // ============================================
    const handleIncomingInvite = (invitation) => {
        // One call at a time — this softphone has no second-line UI, so an
        // INVITE that arrives mid-call is declined as busy rather than silently
        // taking over the session ref and stranding the live call.
        if (sessionRef.current) {
            try {
                invitation.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
            } catch (_) { }
            return;
        }

        sessionRef.current = invitation;

        // Display name first, user part second — the precedence SIPml's
        // getRemoteFriendlyName() used. This value is not just what the card
        // shows: it becomes callDataRef.fromNumber on every CDR event and the
        // argument to searchCustomerByNumber, so inverting it would silently
        // re-key this caller's rows against every historical one.
        //
        // Note this is NOT NameAddrHeader.friendlyName, which falls back to the
        // full aor ("number@realm") rather than the bare user part.
        const incomingNumber = stripTrunkPrefix(
            invitation.remote_identity?.display_name || invitation.remote_identity?.uri?.user || ''
        );
        console.info('[PBX] Incoming INVITE received from', incomingNumber || '(unknown)');
        setRemoteNumber(incomingNumber);
        setCallState('incoming');
        setCallDirection('inbound');

        searchCustomerByNumber(incomingNumber);

        // X-Channel-ID off the INVITE. SIPml made this a hand-rolled walk over
        // msg.ao_headers; SIP.js parses the message and looks headers up by name.
        let channelId = null;
        try {
            channelId = invitation.request?.getHeader('X-Channel-ID') || null;
        } catch (err) {
        }

        const config = sipConfigRef.current;

        pbxCallIdRef.current = null;
        callDataRef.current = {
            sipCallId: null,
            direction: 'inbound',
            fromNumber: incomingNumber,
            toNumber: config?.impi,
            fromExtension: null,
            toExtension: config?.impi,
            fromTag: null,
            toTag: null,
            xChannelId: channelId,
            startTime: new Date().toISOString(),
            answerTime: null,
            endTime: null,
            status: 'ringing'
        };

        captureDialogIdentity(invitation);
        attachSessionHandlers(invitation);

        sendCallEvent('invite_received');
    };

    // ============================================
    // Mute/Unmute
    // ============================================
    const toggleMute = () => {
        if (!sessionRef.current) return;

        const newMuteState = !isMuted;

        // Preferred path: the sender track on the peer connection, which is
        // what actually reaches the other end. Fall back to the local stream
        // for the window before the peer connection has senders.
        if (!applyMuteToSession(sessionRef.current, newMuteState) && localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach((track) => {
                track.enabled = !newMuteState;
            });
        }

        setIsMuted(newMuteState);
    };

    // ============================================
    // Call controls
    // ============================================
    const dialOutbound = async (targetNumber) => {
        if (!userAgentRef.current || !sipConfig || !targetNumber) return;
        if (!userAgentRef.current.isRegistered()) {
            Swal.fire({
                title: 'Not Registered',
                text: 'The PBX connection is not ready yet.',
                icon: 'warning',
                backdrop: 'rgba(0,0,0,0.2)',
                heightAuto: false
            });
            return;
        }
        if (sessionRef.current) return;

        // '#' is not a legal character in a SIP URI user part, so a feature code
        // like *123# has to be escaped before it will parse. The dialpad allows
        // it (handlePhoneNumberChange keeps 0-9 + * #), and '+' and '*' are both
        // legal as-is, so this is the only one that needs encoding.
        const normalizedTarget = String(targetNumber).replace(/#/g, '%23');
        if (!normalizedTarget) {
            Swal.fire({
                title: 'Invalid Number',
                text: `"${targetNumber}" is not a number this PBX can dial.`,
                icon: 'warning',
                backdrop: 'rgba(0,0,0,0.2)',
                heightAuto: false,
                customClass: 'swal-wide',
                width: '450px'
            });
            return;
        }
        const target = `sip:${normalizedTarget}@${sipConfig.realm}`;

        pbxCallIdRef.current = null;
        callDataRef.current = {
            sipCallId: null,
            direction: 'outbound',
            fromNumber: sipConfig.impi,
            toNumber: targetNumber,
            fromExtension: sipConfig.impi,
            toExtension: null,
            fromTag: null,
            toTag: null,
            startTime: new Date().toISOString(),
            answerTime: null,
            endTime: null,
            status: 'initiated'
        };

        let inviter;
        try {
            inviter = userAgentRef.current.call(target, {
                mediaConstraints: PBX_MEDIA_CONSTRAINTS,
                pcConfig: {
                    iceServers: PBX_ICE_SERVERS,
                    iceCandidatePoolSize: 4
                },
                sessionTimersExpires: 0
            });
        } catch (err) {
            console.error('Failed to place outbound call:', err);
            return;
        }

        sessionRef.current = inviter;
        setRemoteNumber(targetNumber);
        setCallState('calling');
        setCallDirection('outbound');

        searchCustomerByNumber(targetNumber);

        attachSessionHandlers(inviter);

        captureDialogIdentity(inviter);
        if (callDataRef.current.sipCallId) {
            callDataRef.current.status = 'inviting';
            sendCallEvent('invite_sent');
        }
    };

    const makeCall = () => {
        if (!sipConfig) {
            Swal.fire({
                title: 'Not Ready',
                text: 'PBX config is still loading. Please wait...',
                icon: 'warning',
                backdrop: 'rgba(0,0,0,0.2)',
                heightAuto: false,
                customClass: 'swal-wide',
                width: '450px'
            });
            return;
        }

        if (!phoneNumber || !userAgentRef.current) {
            Swal.fire({
                title: 'Validation Error',
                text: 'Enter phone number',
                icon: 'warning',
                backdrop: 'rgba(0,0,0,0.2)',
                heightAuto: false,
                customClass: 'swal-wide',
                width: '450px'
            });
            return;
        }

        dialOutbound(phoneNumber);
    };

    // ============================================
    // Supervisor Monitor / Coaching / Conference
    // Silent-monitor, whisper-coach, or barge into another agent's live call
    // by dialing that PBX's feature code (SUPERVISE_FEATURE_CODES) followed
    // by the target agent's extension — e.g. Monitor on ext. 4555 dials
    // 554555. Only reachable when canUseSupervise is true and no call is
    // already in progress.
    // ============================================
    const openSuperviseAgentPicker = (mode) => {
        if (!canUseSupervise) return;
        if (callState !== 'idle') return;
        setSuperviseMode(mode);
    };

    const closeSuperviseAgentPicker = () => setSuperviseMode(null);

    const dialSupervise = (agent) => {
        if (!canUseSupervise || !superviseMode || !agent?.extension) return;

        const prefix = SUPERVISE_FEATURE_CODES[superviseMode];
        const target = `${prefix}${agent.extension}`;

        setSuperviseMode(null);
        dialOutbound(target);
    };

    // applyIceServers() used to live here. SIPml read its ICE configuration out
    // of localStorage at the moment it built a PeerConnection, so the servers
    // had to be re-stamped immediately before answering; SIP.js takes them once
    // in UserAgentOptions.sessionDescriptionHandlerFactoryOptions and hands the
    // same RTCConfiguration to every session it creates.

    const answerCall = () => {
        const session = sessionRef.current;
        if (!session) return;

        // Guards a double Accept click (or the auto-answer timer firing
        // alongside a manual click) from calling session.answer() twice.
        if (answeringRef.current) return;

        stopRingtone();

        if (typeof session.isEnded === 'function' && session.isEnded()) return;

        answeringRef.current = true;
        setIsAnswering(true);

        try {
            session.answer({
                mediaConstraints: PBX_MEDIA_CONSTRAINTS,
                pcConfig: {
                    iceServers: PBX_ICE_SERVERS,
                    iceCandidatePoolSize: 4
                }
            });
        } catch (err) {
            console.error('Failed to answer call:', err);
            answeringRef.current = false;
            setIsAnswering(false);
            if (sessionRef.current === session) handleCallEnd();
        }
    };

    const rejectCall = () => {
        const session = sessionRef.current;
        if (!session) return;

        stopRingtone();
        callDataRef.current.status = 'rejected';
        sendCallEvent('canceled');
        terminateSession(session);
        handleCallEnd();
    };

    const hangup = () => {
        const session = sessionRef.current;
        if (!session) return;

        // An agent hanging up while a REFER is still unresolved means the
        // transfer did not complete on this leg. Drop the pending record before
        // terminating: session.bye() transitions the session to Terminated
        // synchronously, so the Terminated handler's "the PBX BYE'd us right
        // after a successful transfer" fallback would otherwise fire here and
        // label this call 'transferred' in the CDR. (The transfer cleanup timer
        // also calls hangup(), but that path has already cleared the record.)
        if (pendingTransferRef.current) {
            clearTransferPending();
        }

        stopRingtone();
        terminateSession(session);
        handleCallEnd();
    };

    const terminateCall = () => {
        hangup();
    };

    /**
     * Sends DTMF on the established call, via RFC 4733 telephone-event packets
     * on the existing audio stream. SIPml offered no usable equivalent, so the
     * dialpad only ever played a local tone; this is what an IVR on the far end
     * actually hears. Accepts one digit or a string of them.
     */
    const sendDtmf = (tones) => {
        const session = sessionRef.current;
        if (!session || !tones || typeof session.sendDTMF !== 'function') return false;

        try {
            session.sendDTMF(String(tones), {
                transportType: JsSIP.C.DTMF_TRANSPORT.RFC2833
            });
            return true;
        } catch (err) {
            console.error('Failed to send DTMF:', err);
            return false;
        }
    };

    // ============================================
    // Blind transfer — hand the active call off to another agent's extension
    // via SIP REFER. The PBX confirms the outcome asynchronously, in NOTIFY
    // requests sent inside the same dialog; only once one of those reports a
    // final status do we either drop our own leg or tell the agent it didn't go
    // through. Hanging up unconditionally right after REFER would drop the
    // customer whenever the target doesn't answer.
    // ============================================
    const TRANSFER_CONFIRMATION_TIMEOUT_MS = 15000;

    /**
     * Pulls the status code out of a REFER progress NOTIFY.
     *
     * Per RFC 3515 the body is a message/sipfrag whose first line is the status
     * line of the response the referee got back — "SIP/2.0 100 Trying",
     * "SIP/2.0 200 OK", "SIP/2.0 486 Busy Here". Returns null when the body is
     * missing or unparseable, which callers treat as "no verdict yet".
     */
    /**
     * True while the current call has been dialed/offered but not yet answered
     * — the window in which a 'progress' CDR event is still meaningful.
     */
    const isPreAnswerStatus = () => {
        const status = callDataRef.current.status;
        return status === 'inviting' || status === 'ringing' || status === 'initiated';
    };

    const clearTransferPending = () => {
        pendingTransferRef.current = null;
        setIsTransferring(false);
        if (transferTimeoutRef.current) {
            clearTimeout(transferTimeoutRef.current);
            transferTimeoutRef.current = null;
        }
    };

    // The PBX confirms the customer is now bridged to the target. Do NOT hang
    // up here. The session stays Established after the notify — SIP.js does not
    // terminate a session on a successful REFER, and neither did SIPml — and
    // testing showed our own BYE at this point tears down the whole bridge
    // instead of just our leg (likely read by the PBX as an abort, not a
    // completion signal). Just tag the call as transferred and wait for the PBX
    // to end our leg with its own BYE, which the Terminated state change
    // already handles. Not every dialplan config reliably sends that follow-up
    // BYE though — if it never arrives, our leg (and this agent's on_call
    // presence) would be stuck forever, so TRANSFER_LEG_CLEANUP_TIMEOUT_MS
    // below is a fallback that cleans up client-side once the transfer has had
    // time to settle.
    const TRANSFER_LEG_CLEANUP_TIMEOUT_MS = 8000;

    const finishTransferSucceeded = (pending) => {
        if (!pending) return;
        clearTransferPending();
        callDataRef.current.status = 'transferred';
        callDataRef.current.transferTo = pending.extension;
        callDataRef.current.transferToName = pending.displayName;

        const transferredSipCallId = callDataRef.current.sipCallId;
        setTimeout(() => {
            // Guard against a later, unrelated call already occupying
            // sessionRef by the time this fires.
            if (sessionRef.current && callDataRef.current.sipCallId === transferredSipCallId) {
                hangup();
            }
        }, TRANSFER_LEG_CLEANUP_TIMEOUT_MS);
    };

    const finishTransferFailed = (pending) => {
        if (!pending) return;
        clearTransferPending();
        Swal.fire({
            title: 'Transfer Failed',
            text: `${pending.displayName || `Extension ${pending.extension}`} could not be reached. The call was not transferred.`,
            icon: 'error',
            backdrop: 'rgba(0,0,0,0.2)',
            heightAuto: false,
        });
    };

    const transferCall = (targetExtension, targetDisplayName = null) => {
        const session = sessionRef.current;

        if (!session || !sipConfig || callState !== 'active') {
            return;
        }
        if (!targetExtension || pendingTransferRef.current) {
            return;
        }

        const target = `sip:${targetExtension}@${sipConfig.realm}`;
        if (!targetExtension) {
            console.error('Failed to parse transfer target extension:', targetExtension);
            return;
        }

        pendingTransferRef.current = { extension: targetExtension, displayName: targetDisplayName };
        setIsTransferring(true);

        const settle = (outcome) => {
            // The REFER's own pending record — a later transfer attempt on a
            // different call must not be resolved by this one's notify.
            const pending = pendingTransferRef.current;
            if (!pending || pending.extension !== targetExtension) return;
            if (sessionRef.current !== session) return;

            if (outcome === 'failed') {
                finishTransferFailed(pending);
            } else {
                finishTransferSucceeded(pending);
            }
        };

        try {
            session.refer(target, {
                eventHandlers: {
                    requestFailed: () => settle('failed'),
                    accepted: () => settle('succeeded'),
                    failed: () => settle('failed')
                }
            });
        } catch (err) {
            console.error('Failed to transfer call:', err);
            clearTransferPending();
            Swal.fire({
                title: 'Transfer Failed',
                text: 'Could not transfer the call. Please try again.',
                icon: 'error',
                backdrop: 'rgba(0,0,0,0.2)',
                heightAuto: false,
            });
            return;
        }

        // Safety net: if the PBX never reports a final status, don't leave the
        // agent stuck in "transferring" limbo — the call itself stays up.
        transferTimeoutRef.current = setTimeout(() => {
            if (!pendingTransferRef.current) return;
            console.warn('Transfer confirmation timed out; call was not ended locally.');
            clearTransferPending();
            Swal.fire({
                title: 'Transfer Status Unknown',
                text: 'No confirmation was received from the PBX. Please verify with the target agent whether the call went through.',
                icon: 'warning',
                backdrop: 'rgba(0,0,0,0.2)',
                heightAuto: false,
            });
        }, TRANSFER_CONFIRMATION_TIMEOUT_MS);
    };

    const handleCallEnd = () => {
        if (!callDataRef.current.startTime) {
            return;
        }

        stopTimer();
        stopRingtone();
        stopCallRecording();

        const endTime = new Date().toISOString();
        callDataRef.current.endTime = endTime;

        let finalEventType = null;

        if (callDataRef.current.status === 'rejected') {
            finalEventType = null;
        } else if (!callDataRef.current.answerTime) {
            callDataRef.current.status =
                callDataRef.current.direction === 'inbound' ? 'missed' : 'not_answered';
            finalEventType = 'canceled';
        } else {
            finalEventType = 'ended';
        }

        let durationSec = 0;
        if (callDataRef.current.answerTime) {
            durationSec = Math.floor(
                (new Date(endTime) - new Date(callDataRef.current.answerTime)) / 1000
            );
        }

        if (finalEventType) {
            // No dedicated 'transferred' eventType exists on the backend — ride
            // the normal 'ended' event but tag it so a transferred call is still
            // distinguishable in CDR/reporting.
            const extra = callDataRef.current.transferTo
                ? { transferTo: callDataRef.current.transferTo, transferToName: callDataRef.current.transferToName }
                : null;
            sendCallEvent(finalEventType, extra);
        }

        callDataRef.current = {
            sipCallId: null,
            direction: null,
            fromNumber: null,
            toNumber: null,
            fromExtension: null,
            toExtension: null,
            fromTag: null,
            toTag: null,
            startTime: null,
            answerTime: null,
            endTime: null,
            status: null,
            xChannelId: null,
            transferTo: null,
            transferToName: null,
        };

        pbxCallIdRef.current = null;

        setCallDuration(0);
        setCallState('idle');
        setCallDirection(null);
        setIsMuted(false);
        answeringRef.current = false;
        setIsAnswering(false);

        // SIP.js closes the SessionDescriptionHandler as part of terminating a
        // session, and close() stops every receiver AND sender track before
        // closing the RTCPeerConnection — so unlike SIPml there is nothing to
        // reach into here on the normal path.
        //
        // In particular localStreamRef now holds sdh.localMediaStream, i.e. the
        // live sender tracks. It must NOT be stopped here: this runs
        // synchronously from hangup()/rejectCall(), while the BYE is still in
        // flight, so killing the microphone track at this point would cut audio
        // out from under a call that has not finished ending.
        //
        // What remains is a backstop for a session torn down along an unusual
        // path — a failed accept(), say — where nothing ever closed the peer
        // connection. Closing an already-closed connection is a no-op, and the
        // signalingState check keeps this off the normal path entirely.
        try {
            const endingSession = sessionRef.current;
            const terminating =
                typeof endingSession?.isEnded === 'function' && endingSession.isEnded();

            // Only step in when SIP.js is NOT going to. On every normal ending
            // the session is already Terminating/Terminated and its own
            // dispose() closes the connection, so touching it here would just
            // race the BYE still on the wire.
            const pc = endingSession?.connection;
            if (!terminating && pc && pc.signalingState !== 'closed') {
                try { pc.getSenders?.().forEach((s) => s.track && s.track.stop()); } catch (_) { }
                try { pc.close(); } catch (_) { }
            }
        } catch (err) {
        }

        sessionRef.current = null;
        localStreamRef.current = null;
        setRemoteNumber('');
        setCustomerInfo(null);
        // Drop the onaddtrack re-kick attachRemoteAudio installed along with
        // the stream, so a finished call's stream can't keep calling play() on
        // the element the next call is about to use.
        if (audioRemoteRef.current) {
            const previousStream = audioRemoteRef.current.srcObject;
            if (previousStream) previousStream.onaddtrack = null;
            audioRemoteRef.current.pause();
            audioRemoteRef.current.srcObject = null;
        }

        // If the connection died during this call, handleUnexpectedDisconnect
        // deliberately deferred tearing down/reconnecting rather than risk
        // colliding with the live session. Pick that back up now that nothing
        // active is at risk. sipConnect() itself no-ops if a user agent somehow
        // already exists, so this is safe even if we're already connected.
        const orphaned = orphanedUserAgentRef.current;
        if (orphaned) {
            orphanedUserAgentRef.current = null;
            Promise.resolve().then(() => orphaned.stop()).catch(() => { });
        }

        // ...but not when this call is being ended as part of a deliberate
        // disconnect. agentStatusRef still holds the previous value at that
        // point (handleAgentStatusChange's setState has not flushed yet), so
        // without this latch check going Offline mid-call would immediately
        // reconnect the user agent it just tore down.
        if (
            !intentionalStopRef.current &&
            agentStatusRef.current === 'available' &&
            !userAgentRef.current
        ) {
            sipConnect();
        }
    };

    // ============================================
    // Timer
    // ============================================
    const startTimer = () => {
        if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
        }
        setCallDuration(0);
        callTimerRef.current = setInterval(() => {
            setCallDuration((prev) => prev + 1);
        }, 1000);
    };

    const stopTimer = () => {
        if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            callTimerRef.current = null;
        }
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const handlePhoneNumberChange = (value) => {
        const filtered = value.replace(/[^0-9+*#]/g, '');
        const cleaned = filtered.replace(/\+/g, (match, offset) => offset === 0 ? match : '');
        setPhoneNumber(cleaned);
    };

    const handleAutoAnswerToggle = () => {
        const next = !autoAnswer;
        localStorage.setItem('PBXAutoAnswer', JSON.stringify(next));
        setAutoAnswer(next);
    };

    const handleKeypadSoundToggle = () => {
        const next = !keypadSound;
        localStorage.setItem('PBXKeypadSound', JSON.stringify(next));
        setKeypadSound(next);
    };

    const playKeypadTone = (digit) => {
        if (!keypadSound) return;

        const audio = keypadAudioRef.current;
        if (!audio) {
            return;
        }

        try {
            audio.currentTime = 0;
            audio.play().catch(() => { });

            setTimeout(() => {
                audio.pause();
                audio.currentTime = 0;
            }, 200);
        } catch (err) {
        }
    };

    const toggleRecording = () => {
        setIsRecording(prev => !prev);
    };

    // ============================================
    // Handle agent status change (user-initiated)
    // ============================================
    const handleAgentStatusChange = (status, reason = null, metadata = null) => {
        agentStatusVersionRef.current += 1;
        localStorage.setItem('PBXAgentStatus', status);
        // SIP teardown/reconnect callbacks run before React flushes setState.
        // Update the ref synchronously so selecting Offline cannot schedule a
        // reconnect using the previous "available" value.
        agentStatusRef.current = status;
        setAgentStatus(status);

        if (status === 'available') {
            sipConnect();
            return;
        }

        if (status === 'break') {
            sipBreak();
        } else if (status === 'offline') {
            sipDisconnect();
        }

        sendAgentStateChange(status, reason, metadata);
    };

    useEffect(() => {
        if (autoAnswer && callState === 'incoming' && sessionRef.current) {
            const timer = setTimeout(() => {
                if (sessionRef.current && callState === 'incoming') {
                    answerCall();
                }
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [autoAnswer, callState]);

    const value = {
        softPhoneOpen,
        setSoftPhoneOpen,
        registered,
        setRegistered,
        callState,
        setCallState,
        isAnswering,
        canUseSupervise,
        superviseMode,
        openSuperviseAgentPicker,
        closeSuperviseAgentPicker,
        dialSupervise,
        phoneNumber,
        setPhoneNumber,
        remoteNumber,
        setRemoteNumber,
        callDuration,
        setCallDuration,
        isMuted,
        setIsMuted,
        enablePBXCallRingtone,
        setEnablePBXCallRingtone,
        isRingtonePlaying,
        setIsRingtonePlaying,

        audioRemoteRef,
        ringtoneRef,

        makeCall,
        answerCall,
        rejectCall,
        hangup,
        terminateCall,
        sendDtmf,
        transferCall,
        isTransferring,
        presenceAgents,
        presenceLoading,
        fetchAgentPresence,
        toggleMute,
        handlePhoneNumberChange,
        handlePBXCallRingtone,
        formatTime,
        loadPbxConfig,

        sipConfig,
        accountInfo,
        customerInfo,
        agentPermissions,

        autoAnswer, handleAutoAnswerToggle,
        keypadSound, handleKeypadSoundToggle, playKeypadTone,
        isRecording, toggleRecording,
        agentStatus, handleAgentStatusChange,

        sipStatus,
        sipConnect,
        sipDisconnect,
        sipBreak,

        sendAgentStateChange,
        fetchAgentState,
        updateCustomerInfo,
        disconnectForLogout,
        callDirection,
        pbxCallIdRef,
    };

    return (
        <PBXCallState.Provider value={value}>
            {children}
            <audio ref={audioRemoteRef} autoPlay />
        </PBXCallState.Provider>
    );
}

export default PBXCallContext;
